import { z } from 'zod'

import {
  MarketingChannel,
  MarketingTemplateHistoryEventType,
  MarketingTemplateHistorySource,
  MarketingTemplateStatus,
  MarketingType,
} from '../constants/marketing'
import { dbEntitySchema } from './system'
import { marketingEntityScopeSchema } from './marketingScope'

export const marketingTemplateDocumentSchema = z.object({
  emailSubject: z.string().nullish(),
  emailHtml: z.string().nullish(),
  emailText: z.string().nullish(),
  emailDesignJson: z.unknown().nullish(),
  smsText: z.string().nullish(),
  whatsappText: z.string().nullish(),
  mergeTags: z.array(z.string()).nullish(),
  variables: z.array(z.string()).nullish(),
  partials: z.array(z.string()).nullish(),
})

export const marketingTemplateSchema = dbEntitySchema
  .and(marketingEntityScopeSchema)
  .and(
    z.object({
      name: z.string(),
      subject: z.string().nullish(),
      html: z.string(),
      text: z.string().nullish(),
      mergeTags: z.array(z.string()).nullish(),
      status: z.nativeEnum(MarketingTemplateStatus),
      templateDraft: marketingTemplateDocumentSchema.nullish(),
      templatePublished: marketingTemplateDocumentSchema.nullish(),
      templateVersion: z.number().int().min(0).nullish(),
      templateLatestSnapshotVersion: z.number().int().min(0).nullish(),
    })
  )

export const marketingTemplateCreateArgsSchema = marketingEntityScopeSchema.and(
  z.object({
    name: z.string().min(1),
    subject: z.string().nullish().optional(),
    html: z.string().min(1),
    text: z.string().nullish().optional(),
    mergeTags: z.array(z.string()).nullish().optional(),
    status: z.nativeEnum(MarketingTemplateStatus).optional(),
    templateDraft: marketingTemplateDocumentSchema.optional(),
  })
)

export const marketingTemplateUpdateArgsSchema = z.object({
  name: z.string().min(1).optional(),
  subject: z.string().nullish().optional(),
  html: z.string().min(1).optional(),
  text: z.string().nullish().optional(),
  mergeTags: z.array(z.string()).nullish().optional(),
  status: z.nativeEnum(MarketingTemplateStatus).optional(),
  templateDraft: marketingTemplateDocumentSchema.optional(),
  historySource: z.nativeEnum(MarketingTemplateHistorySource).optional(),
})

export const marketingTemplateListQuerySchema = z.object({
  type: z.nativeEnum(MarketingType),
  organizationId: z.string().optional(),
  venueId: z.string().optional(),
})

export const marketingTemplatePublishArgsSchema =
  marketingEntityScopeSchema.and(
    z.object({
      historySource: z.nativeEnum(MarketingTemplateHistorySource).optional(),
    })
  )

export const marketingTemplateHistoryRecordSchema = dbEntitySchema.and(
  z.object({
    marketingTemplate: z.string(),
    kind: z.enum(['event', 'snapshot']),
    eventType: z.nativeEnum(MarketingTemplateHistoryEventType),
    source: z.nativeEnum(MarketingTemplateHistorySource),
    templateVersion: z.number().int().min(0),
    snapshotVersion: z.number().int().min(1).nullable(),
    channel: z.nativeEnum(MarketingChannel).nullable(),
    templateDraft: marketingTemplateDocumentSchema.nullish(),
    templatePublished: marketingTemplateDocumentSchema.nullish(),
    metadata: z.record(z.unknown()).nullish(),
  })
)

export const marketingTemplateVersionSnapshotSchema =
  marketingTemplateHistoryRecordSchema.refine(
    (record) => record.kind === 'snapshot',
    {
      message: 'snapshot record expected',
      path: ['kind'],
    }
  )

export const marketingTemplateVersionListResponseSchema = z.object({
  versions: z.array(marketingTemplateVersionSnapshotSchema),
})

export const marketingTemplateRestoreVersionResponseSchema = z.object({
  template: marketingTemplateSchema,
  restoredVersion: marketingTemplateVersionSnapshotSchema,
})

export const marketingTemplatePreviewArgsSchema =
  marketingEntityScopeSchema.and(
    z.object({
      templateId: z.string(),
      channel: z.nativeEnum(MarketingChannel),
      variables: z.record(z.string()).optional(),
    })
  )

export const marketingTemplatePreviewResponseSchema = z.object({
  subject: z.string().nullish(),
  content: z.string(),
  usedVariables: z.array(z.string()),
  unknownVariables: z.array(z.string()),
})
